import informationSVG from 'assets/images/information.svg'
import theme from 'components/theme'
import { SideType } from 'scenes/MarketData/types'
import styled, { css } from 'styled-components'
import { color } from 'utils'

export const DRAWER_HIDE_DELAY = 500

export const Wrapper = styled.div`
  margin: 12px 0 0 0;
  position: relative;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;

  input[type='number'] {
    appearance: textfield;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export const Label = styled.label`
  position: absolute;
  left: 0.5rem;
  top: 10px;
  color: ${color('dark200')};
  max-width: 8rem;
`

type InputProps = {
  currency?: string
  squaredBottom?: boolean
}
export const Input = styled.input<InputProps>`
  padding: 10px ${(p) => `${(p.currency || '').length}em`} 10px 8rem;
  width: 100%;
  background: ${color('dark700')};
  border-radius: ${(p) => (p.squaredBottom ? '0.5rem 0.5rem 0 0' : '0.5rem')};
  transition: border-radius ${theme.transition.medium};
  border: 0;
  outline: 0;
  text-align: right;
  color: ${color('white')};
  line-height: 20px;

  :hover {
    background: ${color('dark600')};
  }

  :active,
  :focus {
    background: ${color('dark800')};
  }
`

export const InformationIcon = styled.img.attrs(() => ({
  src: informationSVG
}))`
  height: 12px;
  width: 12px;
  vertical-align: middle;
  margin-left: 4px;
`

export const Currency = styled.span`
  position: absolute;
  right: 0.5rem;
  top: 10px;
  color: ${color('dark200')};
`

export const Drawer = styled.div<{ collapsed: boolean }>`
  background: ${color('dark700')};
  color: white;
  border-radius: 0 0 0.5rem 0.5rem;
  overflow: hidden;
  height: auto;
  max-height: ${(p) => (p.collapsed ? '0' : '4rem')};
  transition: max-height ${theme.transition.medium};
`

export const Information = styled.p`
  margin: 4px 0 20px 0;
  color: ${color('dark200')};
  font-weight: 600;
  font-size: 11px;
  line-height: 150%;
`

export const Message = styled.div<{ collapsed: boolean }>`
  display: block;
  padding: 3px 0.75rem;
  border-radius: 0 0 8px 8px;
  overflow: hidden;
  height: ${(p) => (p.collapsed ? '0' : '24px')};
  transition: height 0.3s;

  a {
    cursor: pointer;
  }
`

export const Error = styled.span`
  padding: 3px 12px;
  color: ${color('red400')};
  display: block;

  a {
    text-decoration: underline;
    cursor: pointer;
  }
`

const getCheckBGColor = ({ side }) => {
  if (side === 'buy') return color('green600')
  if (side === 'sell') return color('red600')
  return color('grey100')
}

export const Checkbox = styled(Wrapper)<{ disabled?: boolean; side: SideType; value: boolean }>`
  height: 1.25rem;

  input {
    display: none;
  }

  label {
    cursor: ${(p) => (p.disabled === true ? 'not-allowed' : 'pointer')};
    padding-left: 1.5rem;
    color: ${color('dark200')};
    transition: color ${theme.transition.medium};

    :hover {
      color: ${(p) => (p.disabled === true ? color('dark200') : color('white'))};

      &::before {
        background: ${(p) => (p.disabled === true ? 'auto' : color('dark500'))};
      }
    }
  }

  label::before {
    cursor: ${(p) => (p.disabled === true ? 'not-allowed' : 'pointer')};
    display: block;
    position: absolute;
    margin: 2px;
    content: '';
    background: ${color('dark600')};
    transition: background ${theme.transition.medium};
    border-radius: 4px;
    height: 1rem;
    width: 1rem;
    top: 0;
  }

  img {
    display: none;
    position: absolute;
    left: 4px;
    top: 4px;
  }

  ${(p) =>
    p.value &&
    css`
      label::before {
        background: ${getCheckBGColor};
      }

      img {
        display: inline;
      }
    `}
`

export const RadioOptionSeparator = styled.div<{ shown: Boolean }>`
  width: 1px;
  height: 1rem;
  border-right: 1px solid ${color('overlayWhite100')};
  visibility: ${(p) => (p.shown ? 'visible' : 'hidden')};
`
type LabelProps = { disabled?: boolean; selected: boolean; side: SideType }

const getColor = (props: LabelProps) => {
  if (props.disabled) return color('dark400')
  if (!props.selected) return color('white')
  if (props.side === 'buy') return color('green400')
  return color('red400')
}

const getHoverColor = (props: LabelProps) => {
  if (props.disabled) return getColor(props)
  if (!props.selected) return color('white')
  if (props.side === 'buy') return color('green400')
  return color('red400')
}

const getHoverBgColor = (props) => {
  if (props.disabled) return 'transparent'
  if (props.selected) return color('dark900')
  return color('dark800')
}

const getActiveBgColor = (props) => {
  if (props.disabled) return 'transparent'
  return color('dark900')
}

export const RadioOptionLabel = styled.span<LabelProps>`
  flex: 1 1 auto;
  margin: 2px;
  padding: 4px 0;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
  text-align: center;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  color: ${getColor};
  background-color: ${(props) => (props.selected ? color('dark900') : 'transparent')};
  transition: background-color ${theme.transition.medium}, color ${theme.transition.medium};

  &:hover {
    color: ${getHoverColor};
    background-color: ${getHoverBgColor};
  }

  &:active {
    background-color: ${getActiveBgColor};
  }
`
